import { CheckCircle2, Circle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ThreadTodoItem, ThreadTodoStatus } from "@/api/types";
import { cn } from "@/lib/utils";

interface TodoListProps {
  items: ThreadTodoItem[];
}

function statusIcon(status: ThreadTodoStatus) {
  if (status === "completed")
    return <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success-600" />;
  if (status === "in_progress")
    return (
      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-brand-500" />
    );
  return <Circle className="h-3.5 w-3.5 shrink-0 text-ink-400" />;
}

/**
 * Renderiza a lista de TODOs mutavel do deep agent. ``items`` vem do
 * SSE ``todos_updated`` ou do hook ``useThreadTodos`` no carregamento
 * inicial. Apenas leitura.
 */
export function TodoList({ items }: TodoListProps) {
  const { t } = useTranslation("threads");
  const total = items.length;
  const done = items.filter((i) => i.status === "completed").length;

  if (total === 0) {
    return (
      <div className="rounded-xl border border-dashed border-line bg-surface-muted px-3 py-4 text-center text-xs text-ink-500">
        {t("todos.empty")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h4 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
          {t("todos.title")}
        </h4>
        <span className="text-[10px] text-ink-500">
          {t("todos.counter", { done, total })}
        </span>
      </div>
      <ul className="flex flex-col gap-1.5">
        {items.map((item, idx) => (
          <li
            key={`${idx}-${item.content.slice(0, 24)}`}
            className={cn(
              "flex items-start gap-2 rounded-lg border border-line bg-canvas px-2 py-1.5 text-xs",
              item.status === "completed" && "opacity-70",
            )}
          >
            {statusIcon(item.status)}
            <span
              className={cn(
                "min-w-0 flex-1 break-words",
                item.status === "completed" && "line-through text-ink-500",
              )}
              title={item.content}
            >
              {item.content || "—"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
